import React, { useState, useEffect } from 'react';
import {
  FileText,
  Clock,
  Eye,
  Trash2,
  Copy,
  Check,
  ClipboardList,
  AlertCircle,
  X,
} from 'lucide-react';
import {
  FormSubmission,
  FormTemplate,
  getPetForms,
  getFormTemplates,
  createFormSubmission,
  deleteFormSubmission,
  copyFormLink,
} from '../../utils/formsApi';

interface PetFormsProps {
  petId: string;
  petOwnerId?: string;
}

const PetForms: React.FC<PetFormsProps> = ({ petId, petOwnerId }) => {
  const [forms, setForms] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [viewingForm, setViewingForm] = useState<FormSubmission | null>(null);
  const [deletingForm, setDeletingForm] = useState<FormSubmission | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // New form modal state
  const [showNewFormModal, setShowNewFormModal] = useState(false);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const fetchForms = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPetForms(petId);
      setForms(data);
    } catch (err) {
      setError('Failed to load forms');
      console.error('Failed to fetch pet forms:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, [petId]);

  // Expose handler for Dashboard to trigger the add form modal
  useEffect(() => {
    (window as any)._formsAddHandler = () => {
      handleOpenNewFormModal();
    };
    return () => {
      delete (window as any)._formsAddHandler;
    };
  }, [petOwnerId]);

  const handleCopyLink = async (submissionId: string) => {
    try {
      await copyFormLink(submissionId);
      setCopiedId(submissionId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleDeleteClick = (submission: FormSubmission) => {
    setDeletingForm(submission);
  };

  const handleConfirmDelete = async () => {
    if (!deletingForm) return;
    
    setIsDeleting(true);
    try {
      await deleteFormSubmission(deletingForm.id);
      setDeletingForm(null);
      fetchForms();
    } catch (error) {
      console.error('Failed to delete form:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenNewFormModal = async () => {
    setShowNewFormModal(true);
    setLoadingTemplates(true);
    try {
      // Get all active form templates (not just intake)
      const data = await getFormTemplates(true);
      setTemplates(data);
      if (data.length > 0) {
        setSelectedTemplateId(data[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch form templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showNewFormModal) {
          setShowNewFormModal(false);
          setSelectedTemplateId(null);
        }
        if (viewingForm) {
          setViewingForm(null);
        }
        if (deletingForm) {
          setDeletingForm(null);
        }
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showNewFormModal, viewingForm, deletingForm]);

  const handleCreateForm = async () => {
    if (!selectedTemplateId || !petOwnerId) return;
    
    setIsCreating(true);
    try {
      await createFormSubmission({
        form_template_id: selectedTemplateId,
        pet_owner_id: petOwnerId,
        pet_id: petId,
      });
      setShowNewFormModal(false);
      setSelectedTemplateId(null);
      fetchForms();
    } catch (err) {
      console.error('Failed to create form:', err);
      alert('Failed to create form');
    } finally {
      setIsCreating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'submitted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
            <FileText className="w-3 h-3 mr-1" />
            Submitted
          </span>
        );
      default:
        return status;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading forms...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchForms}
          className="mt-4 text-blue-600 hover:text-blue-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Forms</h2>
          <p className="text-sm text-gray-500 mt-1">
            Customer intake forms and submissions for this pet
          </p>
        </div>
      </div>

      {forms.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No forms</h3>
          <p className="text-gray-500">
            No intake forms have been created for this pet yet.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Form
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Encounter
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Link
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {forms.map((form) => (
                <tr key={form.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-blue-500 mr-3" />
                      <p className="font-medium text-gray-900">
                        {form.template?.name || 'Unknown Form'}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(form.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(form.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    {form.encounter_id ? (
                      <a
                        href={`/pet/${petId}/encounter/${form.encounter_id}/intake`}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-none hover:bg-blue-100 transition-colors whitespace-nowrap"
                      >
                        {form.encounter_date ? new Date(form.encounter_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-') : 'Encounter'}
                      </a>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  {/* Link Column */}
                  <td className="px-6 py-4 text-center">
                    {form.status === 'pending' ? (
                      <button
                        onClick={() => handleCopyLink(form.id)}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        title="Copy form link"
                      >
                        {copiedId === form.id ? (
                          <>
                            <Check className="w-3 h-3 mr-1 text-green-600" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => setViewingForm(form)}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        title="View responses"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </button>
                    )}
                  </td>
                  {/* Delete Column */}
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleDeleteClick(form)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50"
                      title="Delete form"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Modal */}
      {viewingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {viewingForm.template?.name || 'Form Submission'}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  {getStatusBadge(viewingForm.status)}
                  {viewingForm.encounter_id && (
                    <a
                      href={`/pet/${petId}/encounter/${viewingForm.encounter_id}/intake`}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-none hover:bg-blue-100 transition-colors whitespace-nowrap"
                      onClick={() => setViewingForm(null)}
                    >
                      {viewingForm.encounter_date ? new Date(viewingForm.encounter_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-') : 'Encounter'}
                    </a>
                  )}
                </div>
              </div>
              <button
                onClick={() => setViewingForm(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {viewingForm.template?.fields && viewingForm.template.fields.length > 0 ? (
                <div className="space-y-4">
                  {viewingForm.template.fields.map((field: any) => (
                    <div key={field.id} className="border-b border-gray-100 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                      </label>
                      <div className="text-gray-900">
                        {viewingForm.responses?.[field.id] !== undefined ? (
                          <span>
                            {typeof viewingForm.responses[field.id] === 'boolean'
                              ? viewingForm.responses[field.id] ? 'Yes' : 'No'
                              : String(viewingForm.responses[field.id] || '-')}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">No response</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p>No form fields available</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => setViewingForm(null)}
                className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Form</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this form? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingForm(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Form Modal */}
      {showNewFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Form</h3>
              <button
                onClick={() => {
                  setShowNewFormModal(false);
                  setSelectedTemplateId(null);
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {loadingTemplates ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading templates...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 mb-2">No intake form templates available</p>
                  <p className="text-sm text-gray-500">
                    Create an intake form template in Settings → Forms first.
                  </p>
                </div>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Form Template
                  </label>
                  <select
                    value={selectedTemplateId || ''}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  {selectedTemplateId && templates.find(t => t.id === selectedTemplateId)?.description && (
                    <p className="mt-2 text-sm text-gray-500">
                      {templates.find(t => t.id === selectedTemplateId)?.description}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowNewFormModal(false);
                  setSelectedTemplateId(null);
                }}
                disabled={isCreating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateForm}
                disabled={isCreating || !selectedTemplateId || templates.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create Form'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetForms;

